import React, { useMemo } from 'react';
import {
    DataGridPro,
    GridCellParams,
    GridRowParams,
    DataGridProProps,
} from '@mui/x-data-grid-pro';
import LinearProgress from '@mui/material/LinearProgress';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
    CartItem,
    CartList,
    useGetUserInfoQuery,
} from "../../../services/grace";

import containerIcon from "../../../assets/icons/type/container.png"
import fileIcon from "../../../assets/icons/type/file.png"
import tapeIcon from "../../../assets/icons/type/tape.png"

import inIcon from "../../../assets/icons/status/in.png"
import outIcon from "../../../assets/icons/status/out.png"
import destroyedIcon from "../../../assets/icons/status/destroyed.png"
import pendingIcon from "../../../assets/icons/status/pending.png"
import permOutIcon from "../../../assets/icons/status/permout.png"

import {
    buildCustomGrid,
    gridAlignCentre,
    gridDateComparer,
    gridDateOperators,
    gridDateRenderer
} from "../../../util/grid";
import CustomGridToolbar from "../CustomGridToolbar";
import { Box, Link } from "@mui/material";
import { GridColumnVisibilityModel } from "@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces";
import { useAppDispatch, useAppSelector } from "../../../util/hooks";
import { RootState } from "../../../util/store";
import { setColumnVisibility } from "../../../features/inventory/inventorySlice";
import { GridColumnDef, GridColumnTemplate } from "../grid";
import { GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";

function getRowClassName(params: GridRowParams): string {
    return `inventory-grid-theme--${params.row.status}`;
}

function isRowSelectable(params: GridRowParams): boolean {
    return params.row.status !== 'destroyed' && params.row.status !== 'permout';
}

export interface InventoryGridProps extends Omit<DataGridProProps, 'columns' | 'checkboxSelection' | 'components' | 'getRowClassName' | 'isRowSelectable'> {
    columnDef: GridColumnDef[];
    cartData: CartList;
    onBarcodeClick: (event: React.MouseEvent, params: GridCellParams) => void;
    fullResults: boolean;
    retrievedTS?: string;
}

export default function InventoryGrid(props: InventoryGridProps): JSX.Element {
    const {
        rows,
        columnDef,
        cartData,
        onBarcodeClick,
        fullResults,
        retrievedTS,
        ...rest
    } = props;

    const dispatch = useAppDispatch();
    const { data: userData } = useGetUserInfoQuery();
    const columnVisibility = useAppSelector((state: RootState) => state.inventory.columnVisibility);

    const columnTemplate:GridColumnTemplate = useMemo(() => {
        return {
            id: {field: 'id', headerName: 'ID', width: 140, hideable: false},
            view: {
                field: 'view',
                headerName: 'View',
                width: 50,
                disableColumnMenu: true,
                disableExport: true,
                disableReorder: true,
                filterable: false,
                hideable: true,
                resizable: false,
                sortable: false,
                align: gridAlignCentre,
                headerAlign: gridAlignCentre,
                renderHeader: () => (
                    <FontAwesomeIcon size='lg' icon={['far', 'eye']} title='View' style={{cursor: 'pointer'}}/>
                ),
                renderCell: () => {
                    return (
                        <FontAwesomeIcon size='lg' icon={['far', 'eye']} title='View' style={{cursor: 'pointer'}}/>
                    );
                },
            },
            cartStatus: {
                field: 'cartStatus',
                headerName: 'Cart',
                width: 50,
                disableColumnMenu: true,
                disableReorder: true,
                filterable: true,
                hideable: false,
                resizable: false,
                sortable: true,
                align: gridAlignCentre,
                headerAlign: gridAlignCentre,
                renderHeader: () => (
                    <FontAwesomeIcon size='lg' icon={['far', 'shopping-cart']} title='Cart Status'/>
                ),
                renderCell: (params: GridCellParams) => {
                    return (
                      <>
                        {(params.value=="1") ? (
                          <FontAwesomeIcon
                            size="lg"
                            icon={["fas", "shopping-cart"]}
                            title="In Cart"
                            style={{ cursor: "pointer" }}
                          />
                        ) : (
                          <FontAwesomeIcon
                            size="lg"
                            icon={["far", "shopping-cart"]}
                            title="Add to Cart"
                            style={{ cursor: "pointer" }}
                          />
                        )}
                      </>
                    );
                },
            },
            scanId: {
                field: 'scanId',
                headerName: 'Scan-on-demand',
                width: 50,
                disableColumnMenu: true,
                disableReorder: true,
                filterable: true,
                hideable: false,
                resizable: false,
                sortable: true,
                align: gridAlignCentre,
                headerAlign: gridAlignCentre,
                renderHeader: () => (
                    <FontAwesomeIcon size='lg' icon={['far', 'file-image']} title='Scan-on-demand'/>
                ),
                renderCell: (params: GridCellParams) => {
                    if (params.value !== undefined && params.value !== "") {
                        return (<FontAwesomeIcon size='lg' icon={['far', 'file-image']} title='Scan-on-demand'
                                                 style={{cursor: 'pointer'}}/>);
                    } else {
                        return (<div/>);
                    }
                },
            },
            barcode: {
                field: 'barcode',
                headerName: 'Barcode',
                hideable: false,
                width: 140,
                renderCell: (params: GridCellParams) => {
                    return (
                        <Link
                            style={{cursor: 'pointer'}}
                            onClick={(evt: React.MouseEvent) => {
                                onBarcodeClick(evt, params)
                            }}
                        >
                            {params.value}
                        </Link>
                    );
                },

            },
            altCode: {field: 'altCode', headerName: 'Alt. Code', width: 140},
            type: {
                field: 'type',
                headerName: 'Type',
                width: 110,
                type: 'singleSelect',
                valueOptions: [
                    'container',
                    'file',
                    'tape',
                ],
                renderCell: (params: GridCellParams) => {
                    switch (params.value) {
                        case 'container':
                            return (
                                <Box
                                    component={'img'}
                                    sx={{height: 50, width: 50}}
                                    title="Carton"
                                    alt="container"
                                    src={containerIcon}
                                />
                            );
                        case 'file':
                            return (
                                <Box
                                    component={'img'}
                                    sx={{height: 50, width: 50}}
                                    title="File"
                                    alt="file"
                                    src={fileIcon}
                                />
                            );
                        default:
                            return (
                                <Box
                                    component={'img'}
                                    sx={{height: 50, width: 50}}
                                    title="Tape"
                                    alt="tape"
                                    src={tapeIcon}
                                />
                            );
                    }
                },
            },
            status: {
                field: 'status',
                headerName: 'Status',
                width: 120,
                type: 'singleSelect',
                valueOptions: [
                    'in',
                    'out',
                    'pending',
                    'destroyed',
                    'permout',
                ],
                renderCell: (params: GridCellParams) => {
                    switch (params.value) {
                        case 'in':
                            return (
                                "In"
                            );
                        case 'out':
                            return (
                                "Out"
                            );
                        case 'pending':
                            return (
                                "Pending"
                            );
                        case 'permout':
                            return (
                                "Perm. Out"
                            );
                        default:
                            return (
                                "Destroyed"
                            );
                    }
                },
            },
            statusDate: {
                field: 'statusDate',
                headerName: 'Status Date',
                width: 180,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer
            },
            addDate: {
                field: 'addDate',
                headerName: 'Add Date',
                width: 150,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer
            },
            destroyDate: {
                field: 'destroyDate',
                headerName: 'Destroy Date',
                width: 180,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer
            },
            accountLevel1: {field: 'accountLevel1', headerName: 'Account Level 1', width: 180},
            accountLevel2: {field: 'accountLevel2', headerName: 'Account Level 2', width: 180},
            accountLevel3: {field: 'accountLevel3', headerName: 'Account Level 3', width: 180},
            account: {field: 'account', headerName: 'Account', width: 180},
            contentsRangeStart: {field: 'contentsRangeStart', headerName: 'Contents Range Start', width: 220},
            contentsRangeEnd: {field: 'contentsRangeEnd', headerName: 'Contents Range End', width: 210},
            contentsStartDate: {
                field: 'contentsStartDate',
                headerName: 'Contents Start Date',
                width: 210,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer
            },
            contentsEndDate: {
                field: 'contentsEndDate',
                headerName: 'Contents End Date',
                width: 200,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer
            },
            shortDescription: {field: 'shortDescription', headerName: 'Short Description', width: 190},
            longDescription: {field: 'longDescription', headerName: 'Description', width: 180},
            contents: {field: 'contents', headerName: 'Contents', width: 180},
            setName: {field: 'setName', headerName: 'Set Name', width: 180},
            recordSeries: {field: 'recordSeries', headerName: 'Record Series', width: 180},
            containerBarcode: {field: 'containerBarcode', headerName: 'Carton Barcode', width: 200},
            containerAltCode: {field: 'containerAltCode', headerName: 'Carton Alt. Code', width: 210},
            customField1: {field: 'customField1', headerName: 'Custom Field 1', width: 210},
            customField2: {field: 'customField2', headerName: 'Custom Field 2', width: 210},
            customField3: {field: 'customField3', headerName: 'Custom Field 3', width: 210},
            customField4: {field: 'customField4', headerName: 'Custom Field 4', width: 210},
            customDate: {
                field: 'customDate',
                headerName: 'Custom Date',
                width: 150,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                renderCell: gridDateRenderer
            },
            permanent: {
                field: 'permanent',
                headerName: 'Permanent',
                width: 150,
                type: 'date',
                sortComparator: gridDateComparer,
                filterOperators: gridDateOperators,
                align: gridAlignCentre,
                renderCell: (params: GridCellParams) => {
                    if (params.value !== undefined && params.value === 1) {
                        return (<FontAwesomeIcon size='lg' icon={['far', 'lock']} title='Permanent'
                                                 style={{cursor: 'pointer'}}/>);
                    } else {
                        return (<div/>);
                    }
                },
            },
        }
    }, [cartData, onBarcodeClick]);

    const columns = useMemo(() => {
        return buildCustomGrid(columnDef, columnTemplate);
    }, [columnDef, columnTemplate]);

    const handleColumnVisibilityChange = (model: GridColumnVisibilityModel) => {
        dispatch(setColumnVisibility({
            ...model,
            [GRID_CHECKBOX_SELECTION_FIELD]: true,
            id: false,
            cartStatus: userData === undefined || userData?.permissions.orderItems,
        }));
    }

    return (
        <DataGridPro
            {...rest}
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[100, 250, 500, 1000]}
            pagination
            checkboxSelection
            getRowClassName={getRowClassName}
            isRowSelectable={isRowSelectable}
            components={{
                Toolbar: CustomGridToolbar,
                LoadingOverlay: LinearProgress,
            }}
            columnVisibilityModel={columnVisibility}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            componentsProps={{
                toolbar: {
                    resultCount: rows.length,
                    fullResults: fullResults,
                    retrievedTS: retrievedTS,
                }
            }}
            localeText={{
                toolbarExport: 'Export grid results',
                toolbarExportLabel: 'Export grid results',
            }}
        />
    );
}
